import { computed, shallowRef, watch } from "vue";
import { useSpList } from "#ce/components/SpList/composables/useSpList";

/**
 * Owns the items the select offers. The `items` attribute carries them as JSON and the `items`
 * property as an array; either write replaces the supplied list while retaining custom items.
 * A value that is not an array reads as no supplied items, and is reported in development.
 *
 * @param {object} props  Owning component's reactive props; reads `prompt`, `itemTitle` and `itemValue`.
 * @param {{ attrs: Record<string, any>, exposeProperties: (descriptors: object) => void }} options
 *   `attrs` carries the `items` attribute. `exposeProperties` installs the `items` host property.
 * @returns {{ offeredItems: import("vue").ComputedRef<any[]>, items: import("vue").ComputedRef<any[]>,
 *   hasItems: import("vue").ComputedRef<boolean>, addCustomItem: (item: object) => void }} `offeredItems` includes retained custom items. `items`
 *   leads with the prompt row when `prompt` is set.
 */
export function useOfferedItems(props, { attrs, exposeProperties }) {
  const suppliedItems = shallowRef(parseItems(attrs.items));
  const customItems = shallowRef([]);
  const { findItem, getItemTitle, getItemValue } = useSpList(props);
  const offeredItems = computed(() => {
    if (!customItems.value.length) {
      return suppliedItems.value;
    }

    return [
      ...suppliedItems.value,
      ...customItems.value.filter((item) => !findItem(getItemValue(item), suppliedItems.value)),
    ];
  });

  exposeProperties({
    items: {
      get: () => offeredItems.value,
      set: setOfferedItems,
    },
  });

  watch(() => attrs.items, setOfferedItems);

  const items = computed(() => {
    const items = [];

    if (props.prompt) {
      items.push({
        [props.itemTitle]: props.prompt,
        [props.itemValue]: null,
        pendingChoice: false,
      });
    }

    return [...items, ...offeredItems.value];
  });

  const hasItems = computed(() => offeredItems.value.length > 0);

  function setOfferedItems(value) {
    suppliedItems.value = parseItems(value);
  }

  function addCustomItem(item) {
    if (!findItem(getItemValue(item), offeredItems.value)) {
      customItems.value = [...customItems.value, { title: getItemTitle(item), value: getItemValue(item), ...item }];
    }
  }

  return {
    addCustomItem,
    offeredItems,
    items,
    hasItems,
  };
}

function parseItems(value) {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;

    if (parsed == null) {
      return [];
    }

    if (Array.isArray(parsed)) {
      return parsed;
    }

    throw new TypeError("sp-select items must be an array");
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error(error);
    }

    return [];
  }
}
